import { useState, useEffect, useCallback, useRef } from 'react'
import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts'
import { config } from '../config'
import { getAuthScope } from '../constants'

const ORG_STORAGE_KEY = 'portal_organization'

interface OidcUser {
  sub?: string
  email?: string
  name?: string
  nickname?: string
  picture?: string
  // eslint-disable-next-line camelcase
  org_id?: string
}

interface LoginWithRedirectParams {
  authorizationParams?: {
    organization?: string
    // eslint-disable-next-line camelcase
    login_hint?: string
    // eslint-disable-next-line camelcase
    kc_idp_hint?: string
    scope?: string
    prompt?: string
  }
}

interface LogoutParams {
  logoutParams?: {
    returnTo?: string
  }
}

export interface OidcState {
  isAuthenticated: boolean
  isLoading: boolean
  user: OidcUser | undefined
  loginWithRedirect: (params?: LoginWithRedirectParams) => Promise<void>
  logout: (params?: LogoutParams) => Promise<void>
  getAccessTokenSilently: (options?: { authorizationParams?: { scope?: string } }) => Promise<string>
}

function createUserManager(organization?: string): UserManager {
  const authority = config.oidc.domain.startsWith('http')
    ? config.oidc.domain
    : `https://${config.oidc.domain}`

  const extraParams: Record<string, string> = {}
  if (organization) extraParams['organization'] = organization
  if (config.oidc.audience) extraParams['audience'] = config.oidc.audience

  return new UserManager({
    authority,
    // eslint-disable-next-line camelcase
    client_id: config.oidc.clientId,
    // eslint-disable-next-line camelcase
    redirect_uri: window.location.origin,
    scope: getAuthScope(),
    // localStorage so an authenticated session is shared across browser tabs.
    // Visualization links typically open in a new tab; with sessionStorage the
    // new tab had no OIDC user and AuthGuard bounced the user to /login even
    // though they were signed in elsewhere.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    extraQueryParams: extraParams,
  })
}

function extractOidcUser(user: User): OidcUser {
  return {
    sub: user.profile.sub,
    email: user.profile.email,
    name: user.profile.name,
    nickname: user.profile.nickname,
    picture: user.profile.picture,
    // eslint-disable-next-line camelcase
    org_id: (user.profile.org_id as string) || undefined,
  }
}

export function useOidc(): OidcState {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<OidcUser | undefined>(undefined)
  const managerRef = useRef<UserManager | null>(null)
  // Single-flight guard so concurrent silent-sign-in callers share one /token request.
  const silentInFlightRef = useRef<Promise<string> | null>(null)

  const getManager = useCallback((): UserManager => {
    if (!managerRef.current) {
      const org = localStorage.getItem(ORG_STORAGE_KEY) || config.oidc.organization
      managerRef.current = createUserManager(org)
    }
    return managerRef.current
  }, [])

  useEffect(() => {
    let cancelled = false

    const init = async () => {
      const mgr = getManager()

      try {
        const params = new URLSearchParams(window.location.search)
        if (params.has('code') && params.has('state')) {
          // StrictMode-safe: strip code from history before awaiting so the second sync pass sees a clean URL, and ignore `cancelled` after a successful token exchange (the code is single-use).
          const callbackUrl = window.location.href
          window.history.replaceState({}, document.title, window.location.pathname)
          const callbackUser = await mgr.signinRedirectCallback(callbackUrl)
          setUser(extractOidcUser(callbackUser))
          setIsAuthenticated(true)
        } else {
          const existingUser = await mgr.getUser()
          if (!cancelled && existingUser && !existingUser.expired) {
            setUser(extractOidcUser(existingUser))
            setIsAuthenticated(true)
          }
        }
      } catch (err) {
        console.error('OIDC init error:', err)
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void init()
    return () => { cancelled = true }
  }, [getManager])

  const loginWithRedirect = useCallback(async (params?: LoginWithRedirectParams) => {
    const authParams = params?.authorizationParams
    const organization = authParams?.organization
    const scope = authParams?.scope || getAuthScope()

    if (organization) {
      localStorage.setItem(ORG_STORAGE_KEY, organization)
      managerRef.current = createUserManager(organization)
    }

    const mgr = getManager()

    const extraQueryParams: Record<string, string> = {}
    if (organization) extraQueryParams['organization'] = organization
    if (config.oidc.audience) extraQueryParams['audience'] = config.oidc.audience
    // eslint-disable-next-line camelcase
    if (authParams?.login_hint) extraQueryParams['login_hint'] = authParams.login_hint
    // eslint-disable-next-line camelcase
    if (authParams?.kc_idp_hint) extraQueryParams['kc_idp_hint'] = authParams.kc_idp_hint
    if (authParams?.prompt) extraQueryParams['prompt'] = authParams.prompt

    await mgr.signinRedirect({
      scope,
      extraQueryParams,
    })
  }, [getManager])

  const logout = useCallback(async (params?: LogoutParams) => {
    localStorage.removeItem(ORG_STORAGE_KEY)
    const mgr = getManager()
    try {
      const returnTo = params?.logoutParams?.returnTo || window.location.origin
      // id_token_hint lets KC skip the "Do you want to log out?" confirmation.
      const existing = await mgr.getUser()
      await mgr.signoutRedirect({
        // eslint-disable-next-line camelcase
        post_logout_redirect_uri: returnTo,
        // eslint-disable-next-line camelcase
        id_token_hint: existing?.id_token,
      })
    } catch (err) {
      console.error('Logout error:', err)
      await mgr.removeUser()
      window.location.href = params?.logoutParams?.returnTo || window.location.origin
    }
  }, [getManager])

  const getAccessTokenSilently = useCallback(async (
    options?: { authorizationParams?: { scope?: string } },
  ): Promise<string> => {
    const mgr = getManager()

    // Serve existing non-expired token to avoid hammering KC on simultaneous mounts.
    const existingUser = await mgr.getUser()
    if (existingUser?.access_token && !existingUser.expired) {
      return existingUser.access_token
    }

    // Dedupe concurrent silent renewals.
    if (silentInFlightRef.current) {
      return silentInFlightRef.current
    }

    const scope = options?.authorizationParams?.scope || getAuthScope()
    const run = (async (): Promise<string> => {
      try {
        const renewedUser = await mgr.signinSilent({ scope })
        if (renewedUser?.access_token && !renewedUser.expired) {
          setUser(extractOidcUser(renewedUser))
          return renewedUser.access_token
        }
      } catch {
        // Fall through to the expired-session path rather than serving a stale token.
      }
      // KC refused the renewal — wipe the dead user and surface SessionExpired so AuthGuard re-authenticates.
      const err: Error & { name: string } = new Error('Session expired')
      err.name = 'SessionExpired'
      try {
        await mgr.removeUser()
      } catch {
        // Best effort; removal failure must not override the expiry signal.
      }
      setIsAuthenticated(false)
      setUser(undefined)
      throw err
    })().finally(() => {
      silentInFlightRef.current = null
    })

    silentInFlightRef.current = run
    return run
  }, [getManager])

  return {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  }
}
